import axios from 'axios';

// Base API URL
const API_BASE_URL = process.env.REACT_APP_API_URL || (process.env.NODE_ENV === 'production' ? '/api' : 'http://localhost:8000/api');

// Create axios instance with default config
const api = axios.create({
    baseURL: API_BASE_URL,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Request interceptor to add auth token to all requests
api.interceptors.request.use(
    (config) => {
        const token = localStorage.getItem('authToken');
        if (token) {
            config.headers.Authorization = `Token ${token}`;
        }
        if (config.data instanceof FormData) {
            delete config.headers['Content-Type'];
        }
        return config;
    },
    (error) => {
        return Promise.reject(error);
    }
);

// Response interceptor for error handling
api.interceptors.response.use(
    (response) => response,
    (error) => {
        if (error.response?.status === 401) {
            // Unauthorized - clear token and redirect to login
            localStorage.removeItem('authToken');
            localStorage.removeItem('user');
        }
        return Promise.reject(error);
    }
);

// ==================== USER API FUNCTIONS ====================

/**
 * Login user and get authentication token
 * @param {string} username - User's username
 * @param {string} password - User's password
 * @returns {Promise} Response with user data and token
 */
export const loginUser = async (username, password) => {
    try {
        const response = await api.post('/users/login/', { username, password });
        // Store token and user data in localStorage
        if (response.data.token) {
            localStorage.setItem('authToken', response.data.token);
            localStorage.setItem('user', JSON.stringify(response.data.user));
        }
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Logout current user
 * @returns {Promise} Logout response
 */
export const logoutUser = async () => {
    try {
        const response = await api.post('/users/logout/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    } finally {
        // Always clear storage whether request succeeds or fails
        localStorage.removeItem('authToken');
        localStorage.removeItem('user');
    }
};

/**
 * Get list of all users (Admin only)
 * @returns {Promise} Array of users
 */
export const getAllUsers = async () => {
    try {
        const response = await api.get('/users/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new user (Admin only)
 * @param {Object} userData - User data object
 * @param {string} userData.username - Username
 * @param {string} userData.email - Email address
 * @param {string} userData.password - Password
 * @param {string} userData.first_name - First name
 * @param {string} userData.last_name - Last name
 * @returns {Promise} Created user data with token
 */
export const createUser = async (userData) => {
    try {
        const response = await api.post('/users/', userData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get a single user's current data (own profile or admin). Useful for polling fields that can
 * change server-side without the client doing anything -- e.g. new_notification, which a stale
 * login-time-cached copy of the user wouldn't reflect.
 * @param {number} userId - User ID
 * @returns {Promise} The user object
 */
export const getUser = async (userId) => {
    try {
        const response = await api.get(`/users/${userId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update user information
 * @param {number} userId - User ID
 * @param {Object} userData - Updated user data
 * @returns {Promise} Updated user data
 */
export const updateUser = async (userId, userData) => {
    try {
        const response = await api.patch(`/users/${userId}/`, userData);
        // Update local storage if updating current user
        const currentUser = JSON.parse(localStorage.getItem('user') || '{}');
        if (currentUser.id === userId) {
            localStorage.setItem('user', JSON.stringify(response.data));
        }
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete a user (Admin only)
 * @param {number} userId - User ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteUser = async (userId) => {
    try {
        const response = await api.delete(`/users/${userId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Register a new user (Public)
 * @param {Object} registrationData - Registration data
 * @param {string} registrationData.username - Desired username
 * @param {string} registrationData.email - Email address
 * @param {string} registrationData.password - Password
 * @param {string} registrationData.first_name - First name
 * @param {string} registrationData.last_name - Last name
 * @returns {Promise} Created user data with token
 */
export const registerUser = async (registrationData) => {
    try {
        const response = await api.post('/users/register/', registrationData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const checkEmailExists = async (email) => {
    try {
        const response = await api.get('/users/check-email/', {
            params: { email },
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Change user password
 * @param {string} currentPassword - Current password
 * @param {string} newPassword - New password
 * @returns {Promise} Change password response
 */
export const changePassword = async (currentPassword, newPassword) => {
    try {
        const response = await api.post('/users/change-password/', {
            current_password: currentPassword,
            new_password: newPassword,
        });
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};


/**
 * Get current logged-in user from localStorage
 * @returns {Object|null} Current user object or null
 */
export const getCurrentUser = () => {
    try {
        const user = localStorage.getItem('user');
        return user ? JSON.parse(user) : null;
    } catch (error) {
        return null;
    }
};

export const isIntroduced = () => {
    const user = getCurrentUser();
    return user?.introduction_completed || false;
}

export const updateIntroduced = async (introduced) => {
    const user = getCurrentUser();
    if (!user?.id) {
        return null;
    }

    try {
        const updatedUser = await updateUser(user.id, { introduction_completed: introduced });
        return updatedUser;
    } catch (error) {
        // Keep local state consistent if API update fails temporarily.
        user.introduction_completed = introduced;
        localStorage.setItem('user', JSON.stringify(user));
        throw error;
    }
};

/**
 * Check if user is authenticated
 * @returns {boolean} True if user has valid token
 */
export const isAuthenticated = () => {
    return !!localStorage.getItem('authToken');
};

/**
 * Check if current user is admin/staff
 * @returns {boolean} True if user is staff
 */
export const isStaff = () => {
    const user = getCurrentUser();
    return user?.is_staff || false;
};

/**
 * Check if current user is a supervisor (Django admin database access)
 * @returns {boolean} True if user is a supervisor
 */
export const isAdmin = () => {
    const user = getCurrentUser();
    return user?.is_superuser || false;
};

// ==================== USER SETTINGS API FUNCTIONS ====================

/**
 * Get user settings
 * @param {number} userId - User ID
 * @returns {Promise} User settings data
 */
export const getUserSettings = async (userId) => {
    try {
        const response = await api.get(`/users/${userId}/settings/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update user settings
 * @param {number} userId - User ID
 * @param {Object} settingsData - Settings data
 * @param {string} settingsData.theme - Theme name ('light' or 'dark')
 * @returns {Promise} Updated settings data
 */
export const updateUserSettings = async (userId, settingsData) => {
    try {
        const response = await api.patch(`/users/${userId}/settings/update/`, settingsData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// ==================== USER SPECIALIZATION API FUNCTIONS ====================

/**
 * Get user specializations
 * @param {number} userId - User ID
 * @returns {Promise} Array of user specializations
 */
 export const getUserSpecializations = async (userId) => {
    try {
        const response = await api.get(`/users/${userId}/specializations/`); 
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createUserSpecialization = async (userId, specializationData) => {
    try {
        const response = await api.post(`/users/${userId}/specializations/`, specializationData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateUserSpecialization = async (userId, specializationId, specializationData) => {
    try {
        const response = await api.patch(`/users/${userId}/specializations/${specializationId}/`, specializationData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteUserSpecialization = async (userId, specializationId) => {
    try {
        const response = await api.delete(`/users/${userId}/specializations/${specializationId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

// ==================== USER SPECIALIZATION GRADE API FUNCTIONS ====================

/**
 * Get user specialization grades
 * @param {number} userId - User ID
 * @param {number} specializationId - Specialization ID
 * @returns {Promise} Array of user specialization grades
 */
export const getUserSpecializationGrades = async (userId, specializationId) => {
    try {
        const response = await api.get(`/users/${userId}/specializations/${specializationId}/grades/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const createUserSpecializationGrade = async (userId, specializationId, gradeData) => {
    try {
        const response = await api.post(`/users/${userId}/specializations/${specializationId}/grades/`, gradeData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const updateUserSpecializationGrade = async (userId, specializationId, gradeId, gradeData) => {
    try {
        const response = await api.patch(`/users/${userId}/specializations/${specializationId}/grades/${gradeId}/`, gradeData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const deleteUserSpecializationGrade = async (userId, specializationId, gradeId) => {
    try {
        const response = await api.delete(`/users/${userId}/specializations/${specializationId}/grades/${gradeId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};


// ==================== USER INSTITUTION API FUNCTIONS ====================

/** * Get user's institution relation
 * @param {number} userId - User ID
 * @returns {Promise} User institution relation data
 */
export const getUserInstitution = async (userId) => {
    try {
        const response = await api.get(`/users/institutions/${userId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

export const getAllUserInstitutions = async (userId) => {
    try {
        const allUserInsts = await api.get(`/users/institutions/`);
        const response = allUserInsts.data.filter(ui => ui.user === userId);
        return response;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}

/**
 * Get all user-institution email links across every user (Admin only)
 * @returns {Promise} Array of user institution relations
 */
export const getAllUserInstitutionsList = async () => {
    try {
        const response = await api.get('/users/institutions/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}

export const createUserInstitution = async (institutionData) => {
    try {
        const response = await api.post('/users/institutions/', institutionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}

export const updateUserInstitution = async (userId, institutionData) => {
    try {
        const response = await api.patch(`/users/institutions/${userId}/`, institutionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}

export const deleteUserInstitution = async (userId) => {
    try {
        const response = await api.delete(`/users/institutions/${userId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
}


// ==================== INSTITUTION API FUNCTIONS ====================

/**
 * Get list of all private institutions
 * @returns {Promise} Array of institutions
 */
export const getAllInstitutions = async () => {
    try {
        const response = await api.get('/institutions/');
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Get single institution by ID
 * @param {number} institutionId - Institution ID
 * @returns {Promise} Institution data
 */
export const getInstitutionById = async (institutionId) => {
    try {
        const response = await api.get(`/institutions/${institutionId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Create a new institution (Admin only)
 * @param {Object} institutionData - Institution data
 * @param {string} institutionData.name - Institution name
 * @param {string} institutionData.type - Institution type
 * @param {string} institutionData.domain - Email domain
 * @returns {Promise} Created institution data
 */
export const createInstitution = async (institutionData) => {
    try {
        const response = await api.post('/institutions/', institutionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Update an institution (Admin only)
 * @param {number} institutionId - Institution ID
 * @param {Object} institutionData - Updated institution data
 * @returns {Promise} Updated institution data
 */
export const updateInstitution = async (institutionId, institutionData) => {
    try {
        const response = await api.patch(`/institutions/${institutionId}/`, institutionData);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};

/**
 * Delete an institution (Admin only)
 * @param {number} institutionId - Institution ID to delete
 * @returns {Promise} Delete confirmation
 */
export const deleteInstitution = async (institutionId) => {
    try {
        const response = await api.delete(`/institutions/${institutionId}/`);
        return response.data;
    } catch (error) {
        throw error.response?.data || error.message;
    }
};


export default api;
